import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

import {
  fetchOrderFromAPIClient,
  updateOrderItemAPIClient,
  removeOrderItemAPIClient,
  applyCouponCodeClient,
  removeCouponCodeClient,
} from '~/api/order.client';
import type { Order } from '~/types/Order';
import type { CustomizationPayloadItem } from '~/types/Product';
import { apiFetch } from '~/utils/apiFetch';

declare global {
  interface Window {
    __remixOrderToken?: string;
  }
}

interface OrderContextType {
  order: Order | null;
  isFetching: boolean;
  orderToken: string | null;
  setOrderToken: (_token: string | null) => void;
  addToCart: (_variantCode: string, _quantity: number, _customizations?: CustomizationPayloadItem[]) => Promise<void>;
  updateOrderItem: (_id: number, _quantity: number) => void;
  removeOrderItem: (_id: number) => void;
  applyCoupon: (_code: string) => Promise<void>;
  removeCoupon: () => Promise<void>;
  activeCouponCode: string | null;
  setActiveCouponCode: (_code: string | null) => void;
  fetchOrder: () => void;
  resetCart: () => Promise<void>;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

/** Parse document.cookie into a Record (client-only, returns {} on server) */
function parseBrowserCookies(): Record<string, string> {
  if (typeof document === 'undefined') return {};
  return Object.fromEntries(
    document.cookie
      .split(';')
      .map((c) => c.trim().split('='))
      .filter(([k]) => k)
      .map(([k, v]) => [k, decodeURIComponent(v ?? '')])
  );
}

/** Set a cookie (client-only) */
function setCookie(
  name: string,
  value: string,
  options: { path?: string; maxAge?: number; sameSite?: 'Strict' | 'Lax' | 'None'; secure?: boolean } = {}
): void {
  if (typeof document === 'undefined') return;
  const { path = '/', maxAge, sameSite = 'Lax', secure } = options;
  let cookie = `${name}=${encodeURIComponent(value)}; Path=${path}; SameSite=${sameSite}`;
  if (maxAge !== undefined) cookie += `; Max-Age=${maxAge}`;
  if (secure) cookie += '; Secure';
  document.cookie = cookie;
}

/** Delete a cookie (client-only) */
function deleteCookie(name: string, path = '/'): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; Path=${path}; Max-Age=0; SameSite=Lax`;
}

/**
 * The orderToken cookie has two writers: this context writes the raw token via
 * document.cookie, but the checkout action rewrites it through Remix's createCookie,
 * which stores base64-encoded JSON (`btoa('"<token>"')`). Using that serialized value
 * as an API token yields a 404 and needlessly discards a valid cart — decode it first.
 */
function decodeOrderTokenCookie(value: string): string {
  try {
    const decoded = JSON.parse(atob(value));
    if (typeof decoded === 'string') return decoded;
  } catch {
    // Not a Remix-serialized value — it is the raw token already.
  }
  return value;
}

const getCookieToken = (): string | null => {
  const raw = parseBrowserCookies().orderToken;
  return raw ? decodeOrderTokenCookie(raw) : null;
};

const getChannelCode = (): string => parseBrowserCookies().channelCode ?? 'default';

/**
 * Same-origin channel every open tab of the shop listens on, so a cart change in one
 * of them reaches the others. `BroadcastChannel` never delivers a message back to the
 * sender, which is what keeps this from looping.
 */
const CART_SYNC_CHANNEL = 'ak-cart-sync';

/** Cart mutations can fire in bursts (holding the quantity stepper) — coalesce them. */
const CART_SYNC_DEBOUNCE_MS = 400;

type CartSyncMessage = { type: 'cart-changed'; token: string | null };

export const OrderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [orderToken, setOrderToken] = useState<string | null>(null);
  const [activeCouponCode, setActiveCouponCode] = useState<string | null>(null);
  const [channelCode] = useState(getChannelCode);

  const syncChannelRef = useRef<BroadcastChannel | null>(null);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Tell the other tabs their cart is out of date. Called from the mutations only —
   * never from the query itself, or two tabs would keep invalidating each other.
   */
  const notifyOtherTabs = useCallback((token: string | null) => {
    syncChannelRef.current?.postMessage({ type: 'cart-changed', token } satisfies CartSyncMessage);
  }, []);

  // Listen for cart changes made in the other tabs.
  useEffect(() => {
    // Guarded for SSR and for Safari below 15.4, where BroadcastChannel is missing —
    // those browsers simply fall back to the window-focus refetch.
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return undefined;

    const channel = new BroadcastChannel(CART_SYNC_CHANNEL);
    syncChannelRef.current = channel;

    channel.onmessage = (event: MessageEvent<CartSyncMessage>) => {
      const message = event.data;
      if (message?.type !== 'cart-changed') return;

      // The other tab may have created a fresh cart or cleared it — this tab holds its
      // token in state from mount, so without adopting the new one it would go on
      // refetching a cart that is no longer the active one.
      setOrderToken((current) => (current === message.token ? current : message.token));

      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
      syncTimerRef.current = setTimeout(() => {
        // A tab the user is looking at (two windows side by side) refetches straight
        // away; a hidden one is only marked stale and picks the data up on focus, so
        // background tabs cost nothing — one refetch re-fetches every cart item.
        const isVisible = document.visibilityState === 'visible';
        queryClient.invalidateQueries({ queryKey: ['order'], refetchType: isVisible ? 'active' : 'none' });
      }, CART_SYNC_DEBOUNCE_MS);
    };

    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
      channel.close();
      syncChannelRef.current = null;
    };
  }, [queryClient]);

  const createNewOrder = useCallback(async (): Promise<string | null> => {
    try {
      const response = await apiFetch(
        `${window.ENV?.API_URL}/api/v2/shop/orders`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
        },
        { skipAuthRedirect: true }
      );

      if (!response.ok) throw new Error('Failed to create new cart');

      const order = await response.json();
      if (!order?.tokenValue) throw new Error('Missing tokenValue in order response');

      // If Sylius returned a completed order, we can't use it as a cart
      // This shouldn't happen normally, but handle it gracefully
      if (order?.checkoutState === 'completed') {
        console.error('⚠️ Sylius returned a completed order instead of new cart');
        // Clear the token and cookie - user will need to refresh
        deleteCookie('orderToken');
        setOrderToken(null);
        return null;
      }

      const newToken = order.tokenValue;

      // If Sylius returned an existing cart with items (can happen for logged-in users),
      // clear all items to ensure we have an empty cart
      if (order.items?.length > 0) {
        console.warn('⚠️ New cart has existing items, clearing...');
        for (const item of order.items) {
          try {
            await apiFetch(
              `${window.ENV?.API_URL}/api/v2/shop/orders/${newToken}/items/${item.id}`,
              { method: 'DELETE' },
              { skipAuthRedirect: true }
            );
          } catch {
            console.error('Failed to remove item from cart:', item.id);
          }
        }
      }

      setCookie('orderToken', newToken, { maxAge: 2592000 });
      setOrderToken(newToken);
      // A brand new cart replaces whatever token the other tabs are holding.
      notifyOtherTabs(newToken);

      await apiFetch('/api/sync-cart', { method: 'POST', body: newToken }, { skipAuthRedirect: true });

      return newToken;
    } catch (e) {
      console.error('Could not create a new order:', e);
      return null;
    }
  }, [notifyOtherTabs]);

  // Only validate existing cart token on mount - don't create new cart automatically
  useEffect(() => {
    (async () => {
      const remixToken = typeof window !== 'undefined' && window.__remixOrderToken;
      const cookieToken = getCookieToken();
      const initialToken = remixToken || cookieToken;

      if (initialToken) {
        try {
          const order = await fetchOrderFromAPIClient(initialToken, true);
          if (order?.checkoutState === 'completed') {
            // Completed order - clear the invalid token, cart will be created on first add
            console.warn('⚠️ Existing token is for completed order, clearing...');
            deleteCookie('orderToken');
            setOrderToken(null);
          } else {
            setOrderToken(initialToken);
          }
        } catch {
          // Invalid token - clear it, cart will be created on first add
          console.warn('⚠️ Existing token invalid, clearing...');
          deleteCookie('orderToken');
          setOrderToken(null);
        }
      }
      // No token = no cart yet, will be created lazily on first addToCart
    })();
  }, []);

  const orderQuery = useQuery<Order, Error>({
    queryKey: ['order', orderToken, channelCode],
    enabled: !!orderToken,
    queryFn: async () => {
      if (!orderToken) throw new Error('Missing order token');
      return await fetchOrderFromAPIClient(orderToken, true);
    },
    // Catches carts changed where the broadcast cannot reach: another browser, a phone,
    // or the backend itself. `staleTime` on the QueryClient keeps this from firing on
    // every single tab switch.
    refetchOnWindowFocus: true,
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: number; quantity: number; token: string }) => updateOrderItemAPIClient(vars),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['order'] });
      notifyOtherTabs(vars.token);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (vars: { id: number; token: string }) => removeOrderItemAPIClient(vars),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['order'] });
      notifyOtherTabs(vars.token);
    },
  });

  useEffect(() => {
    const data = orderQuery.data;
    if (data) {
      if (data.promotionCoupon?.code) {
        setActiveCouponCode(data.promotionCoupon.code);
      } else if (data.orderPromotionTotal !== 0) {
        setActiveCouponCode('__USED__');
      } else {
        setActiveCouponCode(null);
      }
    }
  }, [orderQuery.data]);

  const updateOrderItem = (id: number, quantity: number) => {
    if (!orderToken) return;
    updateMutation.mutate({ id, quantity, token: orderToken });
  };

  const removeOrderItem = (id: number) => {
    if (!orderToken) return;
    removeMutation.mutate({ id, token: orderToken });
  };

  const applyCoupon = useCallback(
    async (code: string) => {
      if (!orderToken) return;
      await applyCouponCodeClient(orderToken, code);
      queryClient.invalidateQueries({ queryKey: ['order'] });
      notifyOtherTabs(orderToken);
    },
    [orderToken, queryClient, notifyOtherTabs]
  );

  const removeCoupon = useCallback(async () => {
    if (!orderToken) return;
    await removeCouponCodeClient(orderToken);
    queryClient.invalidateQueries({ queryKey: ['order'] });
    notifyOtherTabs(orderToken);
  }, [orderToken, queryClient, notifyOtherTabs]);

  const fetchOrder = useCallback(() => {
    if (!orderToken) return;
    queryClient.invalidateQueries({ queryKey: ['order', orderToken, channelCode] });
  }, [orderToken, channelCode, queryClient]);

  // Add item to cart - creates cart lazily if needed
  const addToCart = useCallback(
    async (variantCode: string, quantity: number, customizations?: CustomizationPayloadItem[]): Promise<void> => {
      let token = orderToken;

      // Create cart if it doesn't exist
      if (!token) {
        token = await createNewOrder();
        if (!token) {
          throw new Error('Failed to create cart');
        }
      }

      const body: Record<string, unknown> = { productVariant: variantCode, quantity };
      if (customizations && customizations.length > 0) {
        body.customizations = customizations;
      }

      // Add item to cart
      const response = await apiFetch(
        `${window.ENV?.API_URL}/api/v2/shop/orders/${token}/items`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
        { skipAuthRedirect: true }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const message =
          data.violations?.[0]?.message || data['hydra:description'] || data.detail || 'Failed to add item to cart';
        throw new Error(message);
      }

      // Refresh cart data
      queryClient.invalidateQueries({ queryKey: ['order'] });
      notifyOtherTabs(token);
    },
    [orderToken, createNewOrder, queryClient, notifyOtherTabs]
  );

  const resetCart = useCallback(async () => {
    queryClient.removeQueries({ queryKey: ['order'] });
    setOrderToken(null);
    setActiveCouponCode(null);
    deleteCookie('orderToken');
    // The other tabs still hold the old token in state — tell them it is gone, or they
    // would keep refetching a cart this one has just dropped.
    notifyOtherTabs(null);
    // Don't create new cart - it will be created lazily on next addToCart
  }, [queryClient, notifyOtherTabs]);

  return (
    <OrderContext.Provider
      value={{
        order: orderQuery.data ?? null,
        isFetching: orderQuery.isFetching,
        orderToken,
        setOrderToken,
        addToCart,
        updateOrderItem,
        removeOrderItem,
        applyCoupon,
        removeCoupon,
        activeCouponCode,
        setActiveCouponCode,
        fetchOrder,
        resetCart,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export const useOrder = (): OrderContextType => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};
